 import React, { useState } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import FAQSchema from '../../FAQSchema';
// Register necessary components
ChartJS.register(
  ArcElement,
  ChartTooltip,
  Legend
);

const TwowheelerLoanEmiCalculator  = () => {
  const [loanAmount, setLoanAmount] = useState(500000);
  const [interestRate, setInterestRate] = useState(8);
  const [loanTenure, setLoanTenure] = useState(5);
  const [monthlyEMI, setMonthlyEMI] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [amortizationSchedule, setAmortizationSchedule] = useState([]);
  const [activeYear, setActiveYear] = useState(null);

  const calculateEMI = () => {
    const principal = loanAmount;
    const annualInterest = interestRate / 100;
    const monthlyInterest = annualInterest / 12;
    const tenureInMonths = loanTenure * 12;

    const emi =
      (principal * monthlyInterest * Math.pow(1 + monthlyInterest, tenureInMonths)) /
      (Math.pow(1 + monthlyInterest, tenureInMonths) - 1);

    const totalAmount = emi * tenureInMonths;
    const totalInterest = totalAmount - principal;

    setMonthlyEMI(emi.toFixed(2));
    setTotalInterest(totalInterest.toFixed(2));
    setTotalAmount(totalAmount.toFixed(2));

    const schedule = [];
    let balance = principal;
    // let currentYear = new Date().getFullYear();
    let currentYear = new Date().getFullYear();

    let currentMonth = 6;

    for (let i = 0; i < tenureInMonths; i++) {
      const interestForMonth = balance * monthlyInterest;
      const principalForMonth = emi - interestForMonth;
      balance -= principalForMonth;

      schedule.push({
        // month: i + 1,
        // year: year,
        month: (currentMonth + i) % 12 + 1, // Ensure month is in range 1-12
        year: currentYear + Math.floor((currentMonth + i) / 12),
        principalPaid: principalForMonth,
        interestPaid: interestForMonth,
        totalPayment: emi,
        balance: balance,
      });

      // if ((i + 1) % 12 === 0) {
      //   year++;
      // }
    }

    setAmortizationSchedule(schedule);
  };

  const handleLoanAmountChange = (e) => {
    const value = e.target.value;
    setLoanAmount(value === '' ? 0 : parseInt(value));
  };

  const handleInterestRateChange = (e) => {
    const value = e.target.value;
    setInterestRate(value === '' ? 0 : parseFloat(value));
  };

  const handleLoanTenureChange = (e) => {
    const value = e.target.value;
    setLoanTenure(value === '' ? 0 : parseInt(value));
  };

  const handleYearToggle = (year) => {
    setActiveYear(activeYear === year ? null : year);
  };

  const data = {
    labels: ['Principal Amount', 'Total Interest'],
    datasets: [
      {
        label: 'EMI Breakdown',
        data: [loanAmount, totalInterest],
        backgroundColor: ['#36A2EB', '#FF6384'],
      },
    ],
  };
function Icon({ id, open }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
        >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
    );
  }
  
  
    const [activeIndex, setActiveIndex] = useState(0);
  
     
    const faqs = [
        {
            question: "What is a Two Wheeler Loan EMI Calculator?",
            answer: "It’s an online tool that helps you calculate the monthly EMI for your two-wheeler loan based on the loan amount, interest rate, and tenure."
          },
          {
            question: "How do I calculate the EMI for a two-wheeler loan?",
            answer: "EMI is calculated using the formula EMI = [P x r x (1+r)^n] / [(1+r)^n-1], where P is the principal amount, r is the monthly interest rate, and n is the loan tenure in months."
          },
          {
            question: "What factors affect my EMI for a two-wheeler loan?",
            answer: "The loan amount, interest rate, tenure, and type of interest (fixed or floating) all impact the EMI."
          },
          {
            question: "Can I adjust the loan amount or tenure in the calculator?",
            answer: "Yes, you can modify both the loan amount and tenure to see how they affect your monthly EMI."
          },
          {
            question: "How does a higher interest rate affect my EMI?",
            answer: "A higher interest rate will increase your EMI amount, making your monthly payments higher."
          },
          {
            question: "Can I change the tenure of the loan in the calculator?",
            answer: "Yes, the calculator allows you to change the loan tenure, which will adjust your EMI accordingly."
          },
          {
            question: "What is the best way to reduce my EMI amount?",
            answer: "To reduce your EMI, you can either opt for a lower loan amount, a longer loan tenure, or look for loans with lower interest rates."
          },
          {
            question: "Can I use the EMI calculator for loans from different lenders?",
            answer: "Yes, the EMI calculator is universal and can be used to calculate payments for any lender as long as you input the correct interest rate and tenure."
          },
          {
            question: "What is an amortization schedule, and how does it help?",
            answer: "An amortization schedule breaks down the repayment of your loan into monthly payments, showing how much goes toward the principal and how much is paid as interest."
          },
          {
            question: "Will my EMI change during the loan tenure?",
            answer: "For loans with a fixed interest rate, the EMI will remain constant throughout the tenure. For floating rate loans, your EMI may change if the interest rate fluctuates."
          },
          {
            question: "How is the interest rate calculated for my loan?",
            answer: "The interest rate is typically based on your credit score, the lender’s policies, and the prevailing market rates."
          },
          {
            question: "Can I repay my loan early?",
            answer: "Yes, you can make prepayments towards the loan, which can reduce your principal and, in turn, your EMI for the remaining term."
          },
          {
            question: "Does a shorter tenure result in a lower EMI?",
            answer: "No, a shorter tenure typically results in a higher EMI, but you will pay off the loan faster and incur less interest."
          },
          {
            question: "What happens if I miss an EMI payment?",
            answer: "Missing an EMI may incur a penalty fee and negatively affect your credit score."
          },
          {
            question: "Can the EMI calculator predict the exact final payment amount?",
            answer: "Yes, it can provide an accurate estimate, but the actual amount may vary depending on any changes in loan terms during the repayment period."
          }
        ]
    const toggleAccordion = (index) => {
      if (activeIndex === index) {
          // If the clicked question is already expanded, collapse it
          setActiveIndex(null);
      } else {
          // If the clicked question is not expanded, expand it
          setActiveIndex(index);
      };
    }
    const calculators = [
      {
        name: 'SBI Bike Loan Calculator',
        path: '/financial-tools-calculators/sbi-bank-bike-loan-emi-calculator',
        image: '/assets/sbii.png',
      },
      {
        name: 'HDFC Bike Loan Calculator',
        path: '/financial-tools-calculators/hdfc-bank-bike-loan-emi-calculator',
        image: '/assets/hdd.png',
      },
      {
        name: 'ICICI Bike Loan Calculator',
        path: '/financial-tools-calculators/icici-bank-bike-loan-emi-calculator',
        image: '/assets/icc.png',
      },
      {
        name: 'Axis Bike Loan Calculator',
        path: '/financial-tools-calculators/axis-bank-bike-loan-emi-calculator',
        image: '/assets/axis.png',
      },
      {
        name: 'Bajaj Bike Loan Calculator',
        path: '/financial-tools-calculators/bajaj-finserv-bank-bike-loan-emi-calculator',
        image: '/assets/baj.png',
      },
      {
        name: 'Kotak Bike Loan Calculator',
        path: '/financial-tools-calculators/kotak-mahindra-bank-bike-loan-emi-calculator',
        image: '/assets/kot.png',
      },
      {
        name: 'Tata Capital Bike Loan Calculator',
        path: '/financial-tools-calculators/tata-capital-bank-bike-loan-emi-calculator',
        image: '/assets/tat.png',
      },
      {
        name: 'Mahindra Finance Bike Loan Calculator',
        path: '/financial-tools-calculators/mahindra-finance-bank-bike-loan-emi-calculator',
        image: '/assets/ma.png',
      },
      {
        name: 'Muthoot FinanceBike Loan Calculator',
        path: '/financial-tools-calculators/muthoot-finance-bank-bike-loan-emi-calculator',
        image: '/assets/mu.png',
      },
    ];
  return (
    <>
     <Helmet>
         <title>Two Wheeler Loan EMI Calculator – Bike Loan EMI & Interest</title>
        <meta  name='description' content='Estimate EMI and interest for two-wheeler loans. Plan your repayments easily with our loan calculator.'/>
        <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/car-loan-emi-calculator" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <FAQSchema faqs={faqs} />
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
      <ol className="list-none p-0 inline-flex">
        <li className="flex items-center">
          <Link to="/" className="text-blue-600 hover:text-blue-800 ">Home</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
        <li className="flex items-center">
          <Link to="/financial-tools-calculators" className="text-blue-600 hover:text-blue-800">Financial Tools Calculators</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
          <li className="flex items-center">
          <span className="text-gray-500 ">Two Wheeler Loan EMI Calculator</span>
        </li> 
      
      </ol>
    </nav>
        <div className='py-4'>
    <div className="container mx-auto p-4 shadow-xl rounded-md">
      <h1 className="text-2xl font-bold mb-4">Two Wheeler Loan EMI Calculator</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Loan Amount</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                value={loanAmount}
                onChange={handleLoanAmountChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">₹ {loanAmount.toLocaleString()}</span>
            </div>
            <input
              type="range"
              min="0"
              max="10000000"
              value={loanAmount}
              onChange={(e) => setLoanAmount(parseInt(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Rate of Interest (P.A.)</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={interestRate}
                onChange={handleInterestRateChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">{interestRate} %</span>
            </div>
            <input
              type="range"
              min="0"
              max="15"
              step="0.1"
              value={interestRate}
              onChange={(e) => setInterestRate(parseFloat(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Loan Tenure (Years)</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                max="30"
                value={loanTenure}
                onChange={handleLoanTenureChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">{loanTenure} Yr</span>
            </div>
            <input
              type="range"
              min="0"
              max="30"
              value={loanTenure}
              onChange={(e) => setLoanTenure(parseInt(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <button
            onClick={calculateEMI}
            className="bg-blue-500 text-white px-4 py-2 rounded"
          >
            Calculate
          </button>
        </div>
        <div>
          <div className="mb-4">
            <h2 className="text-xl font-bold">Results</h2>
            <p>Monthly EMI: ₹ {parseFloat(monthlyEMI).toLocaleString()}</p>
            <p>Total Principal Amount: ₹ {loanAmount.toLocaleString()}</p>
            <p>Total Interest: ₹ {parseFloat(totalInterest).toLocaleString()}</p>
            <p>Total Amount (Principal + Interest): ₹ {parseFloat(totalAmount).toLocaleString()}</p>
          </div>
          <div>
            <h2 className="text-xl font-bold">Breakdown</h2>
            <div className="flex justify-center">
              <Pie data={data} options={{ maintainAspectRatio: false }} width={300} height={300} />
            </div>
          </div>
        </div>
      </div>
      <div className="mt-6">
        <h2 className="text-xl font-bold mb-4">Amortization Schedule</h2>
        {amortizationSchedule.length > 0 && (
          <div className="flex flex-col space-y-4">
            {Array.from(new Set(amortizationSchedule.map(item => item.year))).map((year, idx) => (
              <div key={idx} className="mb-4 border rounded-md shadow-md">
                <div
                  className={`cursor-pointer text-lg font-semibold bg-gray-100 p-4 flex justify-between items-center ${activeYear === year ? 'bg-blue-100' : ''}`}
                  onClick={() => handleYearToggle(year)}
                >
                  <span>{year}</span>
                  <span>{activeYear === year ? '-' : '+'}</span>
                </div>
                {activeYear === year && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white rounded-md shadow-md">
                      <thead>
                        <tr>
                          <th className="py-2 px-4 border-b">Month</th>
                          <th className="py-2 px-4 border-b">Principal Paid</th>
                          <th className="py-2 px-4 border-b">Interest Charged</th>
                          <th className="py-2 px-4 border-b">Total Payment</th>
                          <th className="py-2 px-4 border-b">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {amortizationSchedule.filter(item => item.year === year).map((item, idx) => (
                          <tr key={idx}>
                            <td className="py-2 px-4 border-b">{new Date(0, item.month - 1).toLocaleString('default', { month: 'short' })}</td>
                            <td className="py-2 px-4 border-b">₹ {item.principalPaid.toFixed(2).toLocaleString()}</td>
                            <td className="py-2 px-4 border-b">₹ {item.interestPaid.toFixed(2).toLocaleString()}</td>
                            <td className="py-2 px-4 border-b">₹ {item.totalPayment.toFixed(2).toLocaleString()}</td>
                            <td className="py-2 px-4 border-b">₹ {item.balance.toFixed(2).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>


    <div className="p-4 sm:p-8 bg-white">
      <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
      Two Wheeler Loan EMI Calculator 
      </h2>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
             {calculators.map((calc, index) => (
               <motion.div
                 key={index}
                 whileHover={{ scale: 1.04 }}
                 initial={{ opacity: 0, y: 20 }}
                 animate={{ opacity: 1, y: 0 }}
                 transition={{ duration: 0.3, delay: index * 0.05 }}
               >
                 <Link
                   to={calc.path}
                   className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
                 >
                   <img
                     src={calc.image}
                     alt={calc.name}
                     className="w-16 h-16 object-contain mb-2"
                   />
                   <span className="text-md font-medium text-gray-800 break-words leading-snug">
                     {calc.name}
                   </span>
                 </Link>
               </motion.div>
             ))}
           </div>
    </div>
     <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6" >
     <div className="w-full md:w-2/3 p-4 mx-auto">
     <div className="container mx-auto py-4">
          <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            Plan Your Two Wheeler Loan Payments with TheFinMaster’s EMI Calculator
          </h1>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            Buying a two-wheeler is often a significant financial decision, and if you are considering financing your purchase, a two-wheeler loan is an excellent way to make your dream of owning a bike or scooter come true. However, managing a loan’s repayment schedule requires careful planning. With TheFinMaster’s Two Wheeler Loan EMI Calculator, you can easily estimate your monthly payments based on the loan amount, interest rate, and tenure. This tool simplifies the process of loan planning, helping you manage your finances with ease.
          </p>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            By using the Two Wheeler Loan EMI Calculator, you get to know exactly how much you’ll be paying each month, avoiding any surprises when the repayment begins. It helps you make informed decisions and provides transparency about your financial obligations, allowing you to comfortably manage your loan repayment.
          </p>
</div>
          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is a Two Wheeler Loan EMI?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              An EMI (Equated Monthly Installment) is the fixed monthly amount you need to repay your loan over the agreed tenure. When you opt for a two-wheeler loan, you are borrowing a certain amount from a lender to buy your vehicle, and in return, you need to repay the amount in monthly installments over a specified period.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The EMI consists of two components:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Principal</strong> – The original amount you borrowed.</li>
              <li><strong>Interest</strong> – The cost of borrowing, calculated based on the loan amount, interest rate, and tenure.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The amount of EMI depends on three main factors:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Loan Amount:</strong> The more you borrow, the higher your EMI.</li>
              <li><strong>Interest Rate:</strong> Higher interest rates lead to higher EMIs.</li>
              <li><strong>Tenure:</strong> A longer tenure reduces your monthly EMI but increases the total interest paid over the loan term.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Example:</strong><br />
              If you take a loan of ₹1,00,000 at an interest rate of 10% per annum for 3 years, your EMI will be calculated based on these factors.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Why Use TheFinMaster’s Two Wheeler Loan EMI Calculator?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Using TheFinMaster’s Two Wheeler Loan EMI Calculator offers a host of benefits that will help you plan your finances better and make your loan process more transparent. Here are some compelling reasons to use the calculator:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Instant EMI Calculation:</strong> The tool provides an instant calculation of your EMI once you input the loan amount, tenure, and interest rate. It gives you a quick estimate of the monthly repayments.</li>
              <li><strong>Customizable Loan Parameters:</strong> You can easily adjust the loan amount, tenure, and interest rate to see how changes in these factors affect your EMI. This flexibility helps you tailor the loan to fit your budget.</li>
              <li><strong>Budget Planning:</strong> By understanding your EMI in advance, you can budget your monthly expenses accordingly, ensuring that your loan repayment fits into your financial plans without straining your finances.</li>
              <li><strong>Interest Breakdown:</strong> The EMI Calculator not only gives you the monthly repayment amount but also shows the total interest you’ll pay over the loan tenure, allowing you to plan better.</li>
              <li><strong>No Hidden Charges:</strong> With TheFinMaster’s calculator, what you see is what you get. There are no hidden fees or unexpected charges. It provides accurate results based on real-time data and is reliable for accurate loan estimations.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How Does TheFinMaster’s Two Wheeler Loan EMI Calculator Work?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The working of TheFinMaster’s Two Wheeler Loan EMI Calculator is simple and straightforward. It uses the EMI calculation formula, which factors in your loan amount, interest rate, and tenure. Here’s how it works:
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Input Parameters:</strong> You input the loan amount, tenure (in months), and the interest rate (annual rate).
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>EMI Calculation:</strong> Based on these inputs, the calculator uses the formula to determine your EMI amount.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Formula for EMI Calculation:</strong><br />
              EMI = [P x r x (1+r)^n] / [(1+r)^n-1]
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Where:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>P = Loan Amount</li>
              <li>r = Monthly Interest Rate (Annual Interest Rate divided by 12)</li>
              <li>n = Loan Tenure in Months</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Example:</strong><br />
              Let’s assume you take a two-wheeler loan of ₹1,00,000 at an interest rate of 10% annually for 3 years.<br />
              Principal (P) = ₹1,00,000<br />
              Annual Interest Rate = 10%<br />
              Monthly Interest Rate (r) = 10% / 12 = 0.00833<br />
              Loan Tenure (n) = 36 months (3 years)<br />
              Now, applying the formula, we get the EMI to be ₹3,221.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Factors Affecting Your Two Wheeler Loan EMI
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Your EMI is determined by a few key factors that can have a significant impact on your loan repayments. Understanding these factors will help you better plan and manage your loan. Here are the most important factors:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Loan Amount:</strong> The larger the loan amount, the higher the EMI. If you borrow a larger amount, your monthly repayments will be more significant. Conversely, if you borrow a smaller amount, your EMI will be lower.</li>
              <li><strong>Interest Rate:</strong> The interest rate plays a major role in determining your EMI. A higher interest rate increases the EMI, and a lower rate brings it down. The interest rate is typically determined based on your credit score and the lender’s terms.</li>
              <li><strong>Tenure (Loan Period):</strong> The tenure refers to the length of time over which you’ll repay the loan. A longer tenure lowers the EMI, but it also increases the total interest paid over the life of the loan. A shorter tenure raises your EMI, but you pay off the loan faster and incur less total interest.</li>
              <li><strong>Prepayment:</strong> Some loans allow for prepayment, which means you can pay off the loan before the end of the tenure. This reduces your principal amount, which, in turn, can lower your future EMI payments.</li>
              <li><strong>Type of Interest (Fixed or Floating):</strong> Loans with fixed interest rates have the same EMI throughout the tenure, while loans with floating rates may result in changes in the EMI due to fluctuations in interest rates.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Benefits of Using TheFinMaster’s Two Wheeler Loan EMI Calculator
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Using an EMI calculator for your two-wheeler loan offers several benefits:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Quick Results:</strong> The calculator provides results instantly, so you don't have to wait for approval or manual calculation.</li>
              <li><strong>Easy to Use:</strong> The interface is user-friendly, making it easy for anyone to use, even those with no experience in finance.</li>
              <li><strong>Clarity in Planning:</strong> With accurate and instant EMI calculation, you can plan your monthly budget and ensure your repayment schedule fits within your financial capacity.</li>
              <li><strong>Comparison of Different Loan Terms:</strong> You can experiment with different loan amounts, tenures, and interest rates to find the combination that suits your needs and budget.</li>
              <li><strong>Accurate and Reliable:</strong> TheFinMaster’s calculator uses precise formulas, ensuring you get accurate EMI figures that can be trusted.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Amortization Schedule for Two Wheeler Loan
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              An amortization schedule provides a detailed breakdown of each EMI you need to pay for the loan. It shows how much of each payment goes toward the principal (the original loan amount) and how much is used to pay the interest. In the beginning, the interest component is larger, and over time, more of your EMI goes toward repaying the principal.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              This schedule helps you keep track of your loan balance and the reduction in outstanding debt. Here’s an example of an amortization schedule for a ₹1,00,000 loan at 10% annual interest for 3 years (36 months). For simplicity, the sample below shows the first 12 months.
            </p>
            {/* <div className="overflow-x-auto">
              <table className="w-full text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-4 border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-4 py-2 text-left">Month</th>
                    <th className="border px-4 py-2 text-left">EMI Amount (₹)</th>
                    <th className="border px-4 py-2 text-left">Principal Repayment (₹)</th>
                    <th className="border px-4 py-2 text-left">Interest Payment (₹)</th>
                    <th className="border px-4 py-2 text-left">Remaining Loan Balance (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border px-4 py-2">1</td>
                    <td className="border px-4 py-2">3,221</td>
                    <td className="border px-4 py-2">2,221</td>
                    <td className="border px-4 py-2">1,000</td>
                    <td className="border px-4 py-2">97,779</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">2</td>
                    <td className="border px-4 py-2">3,221</td>
                    <td className="border px-4 py-2">2,230</td>
                    <td className="border px-4 py-2">991</td>
                    <td className="border px-4 py-2">95,549</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">3</td>
                    <td className="border px-4 py-2">3,221</td>
                    <td className="border px-4 py-2">2,239</td>
                    <td className="border px-4 py-2">982</td>
                    <td className="border px-4 py-2">93,310</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">4</td>
                    <td className="border px-4 py-2">3,221</td>
                    <td className="border px-4 py-2">2,248</td>
                    <td className="border px-4 py-2">973</td>
                    <td className="border px-4 py-2">91,062</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">5</td>
                    <td className="border px-4 py-2">3,221</td>
                    <td className="border px-4 py-2">2,257</td>
                    <td className="border px-4 py-2">964</td>
                    <td className="border px-4 py-2">88,804</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">6</td>
                    <td className="border px-4 py-2">3,221</td>
                    <td className="border px-4 py-2">2,266</td>
                    <td className="border px-4 py-2">955</td>
                    <td className="border px-4 py-2">86,538</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">7</td>
                    <td className="border px-4 py-2">3,221</td>
                    <td className="border px-4 py-2">2,275</td>
                    <td className="border px-4 py-2">946</td>
                    <td className="border px-4 py-2">84,263</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">8</td>
                    <td className="border px-4 py-2">3,221</td>
                    <td className="border px-4 py-2">2,284</td>
                    <td className="border px-4 py-2">937</td>
                    <td className="border px-4 py-2">81,979</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">9</td>
                    <td className="border px-4 py-2">3,221</td>
                    <td className="border px-4 py-2">2,293</td>
                    <td className="border px-4 py-2">928</td>
                    <td className="border px-4 py-2">79,686</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">10</td>
                    <td className="border px-4 py-2">3,221</td>
                    <td className="border px-4 py-2">2,302</td>
                    <td className="border px-4 py-2">919</td>
                    <td className="border px-4 py-2">77,384</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">11</td>
                    <td className="border px-4 py-2">3,221</td>
                    <td className="border px-4 py-2">2,311</td>
                    <td className="border px-4 py-2">910</td>
                    <td className="border px-4 py-2">75,073</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">12</td>
                    <td className="border px-4 py-2">3,221</td>
                    <td className="border px-4 py-2">2,320</td>
                    <td className="border px-4 py-2">901</td>
                    <td className="border px-4 py-2">72,753</td>
                  </tr>
                </tbody>
              </table>
            </div> */}
            <div className="mt-6">
  {/* Table view for medium and up */}
  <div className="hidden md:block overflow-x-auto">
    <table className="w-full text-lg text-gray-600 mt-4 border-collapse">
      <thead>
        <tr className="bg-gray-100">
          <th className="border px-4 py-2 text-left">Month</th>
          <th className="border px-4 py-2 text-left">EMI Amount(₹)</th>
          <th className="border px-4 py-2 text-left">Principal Repayment(₹)</th>
          <th className="border px-4 py-2 text-left">Interest Payment (₹)</th>
          <th className="border px-4 py-2 text-left">Remaining Loan Balance  (₹)</th>
        </tr>
      </thead>
      <tbody>
        {[
          
       
          ["1", "3,221", "2,221", "1,000", "97,779"],
          ["2", "3,221", "2,230", "991", "95,549"],
          ["3", "3,221", "2,239", "982", "93,310"],
          ["4", "3,221", "2,248", "973", "91,062"],
          ["5", "3,221", "2,257", "964", "88,804"],
          ["6", "3,221", "2,266", "955", "86,538"],
          ["7", "3,221", "2,275", "946", "84,263"],
          ["8", "3,221", "2,284", "937", "81,979"],
          ["9", "3,221", "2,293", "928", "79,686"],
          ["10", "3,221", "2,302", "919", "77,384"],
          ["11", "3,221", "2,311", "910", "75,073"],
          ["12", "3,221", "2,320", "901", "72,753"]
     
        ].map(([month, emi, interest, principal, balance], index) => (
          <tr key={index}>
            <td className="border px-4 py-2">{month}</td>
            <td className="border px-4 py-2">{emi}</td>
            <td className="border px-4 py-2">{interest}</td>
            <td className="border px-4 py-2">{principal}</td>
            <td className="border px-4 py-2">{balance}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>

  {/* Card layout for small screens */}
  <div className="md:hidden space-y-4 mt-4">
    {[
        ["1", "3,221", "2,221", "1,000", "97,779"],
        ["2", "3,221", "2,230", "991", "95,549"],
        ["3", "3,221", "2,239", "982", "93,310"],
        ["4", "3,221", "2,248", "973", "91,062"],
        ["5", "3,221", "2,257", "964", "88,804"],
        ["6", "3,221", "2,266", "955", "86,538"],
        ["7", "3,221", "2,275", "946", "84,263"],
        ["8", "3,221", "2,284", "937", "81,979"],
        ["9", "3,221", "2,293", "928", "79,686"],
        ["10", "3,221", "2,302", "919", "77,384"],
        ["11", "3,221", "2,311", "910", "75,073"],
        ["12", "3,221", "2,320", "901", "72,753"]
    ].map(([month, emi, interest, principal, balance], index) => (
      <div key={index} className="bg-white shadow rounded-xl p-4 text-sm text-gray-700 space-y-1 border">
        <div><span className="font-semibold">Month:</span> {month}</div>
        <div><span className="font-semibold">EMI Amount (₹):</span> {emi}</div>
        <div><span className="font-semibold">Principal Repayment(₹):</span> {interest}</div>
        <div><span className="font-semibold">Interest Payment (₹):</span> {principal}</div>
        <div><span className="font-semibold">Remaining Loan Balance (₹):</span> {balance}</div>
      </div>
    ))}
  </div>
</div>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              In this table:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>EMI Amount:</strong> The fixed amount you need to pay monthly.</li>
              <li><strong>Principal Repayment:</strong> The portion of the EMI that goes towards reducing your loan principal.</li>
              <li><strong>Interest Payment:</strong> The portion of the EMI that goes toward interest.</li>
              <li><strong>Remaining Loan Balance:</strong> The outstanding loan amount after each repayment.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              As you can see, the principal repayment portion increases each month, while the interest payment decreases.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Factors Affecting Your Two Wheeler Loan EMI
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Several key factors impact the EMI you will pay on your two-wheeler loan. Understanding these factors is crucial for making an informed decision when choosing the right loan. Here’s a breakdown of the most important factors:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Loan Amount:</strong> The loan amount is one of the most influential factors in determining your EMI. A larger loan will result in higher EMIs, while a smaller loan will reduce the EMI. It’s important to borrow only what you need to avoid unnecessary debt.<br />
                <strong>Example:</strong> If you borrow ₹1,50,000, your EMI will be higher than if you borrow ₹1,00,000 due to the increased loan amount.
              </li>
              <li><strong>Interest Rate:</strong> The interest rate directly affects the total cost of the loan. A higher interest rate leads to higher EMIs because the lender is charging more to borrow the money. A lower interest rate reduces the EMI, making the loan more affordable over time.<br />
                <strong>Example:</strong> An interest rate of 12% per annum will result in a higher EMI than an interest rate of 8% for the same loan amount and tenure.
              </li>
              <li><strong>Loan Tenure:</strong> The loan tenure is the period over which you’ll repay the loan. If you opt for a longer tenure, the EMI will be lower, but you’ll pay more in total interest. Conversely, choosing a shorter tenure increases your EMI but reduces the overall interest paid.<br />
                <strong>Example:</strong> For a ₹1,00,000 loan at 10% interest, a 36-month tenure would have a higher EMI than a 60-month tenure, but the total interest paid will be less for the shorter tenure.
              </li>
              <li><strong>Type of Interest Rate (Fixed or Floating):</strong><br />
                <strong>Fixed Interest Rate:</strong> Your EMI remains constant throughout the loan tenure. This gives you predictability and stability in your monthly payments.<br />
                <strong>Floating Interest Rate:</strong> The EMI may change if the interest rate fluctuates during the loan tenure. While this can lead to potential savings when rates go down, it can also increase your monthly EMI if rates rise.
              </li>
              <li><strong>Prepayment:</strong> Some lenders allow you to make early repayments, which can lower your outstanding principal. Prepaying a part of the loan can reduce the loan tenure or the EMI, helping you save on interest payments.<br />
                <strong>Example:</strong> If you prepay ₹50,000 toward your loan, your EMI will decrease, or the loan tenure can be shortened, depending on the lender's policy.
              </li>
              <li><strong>Processing Fee:</strong> While the processing fee doesn’t affect the EMI directly, it can increase the total loan amount and, consequently, the EMI. Some lenders charge a fee to process the loan application, which is added to the principal.
              </li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Difference Between Two Wheeler Loan and Personal Loan
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Both two-wheeler loans and personal loans are forms of borrowing, but they differ in terms of purpose, eligibility, interest rates, and repayment structure. Here’s a comparison to help you understand their key differences:
            </p>
            {/* <div className="overflow-x-auto">
              <table className="w-full text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-4 border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-4 py-2 text-left">Feature</th>
                    <th className="border px-4 py-2 text-left">Two Wheeler Loan</th>
                    <th className="border px-4 py-2 text-left">Personal Loan</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border px-4 py-2">Purpose</td>
                    <td className="border px-4 py-2">Specifically used to finance the purchase of a two-wheeler.</td>
                    <td className="border px-4 py-2">Can be used for any personal need, including vacations, medical expenses, or home renovation.</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">Collateral</td>
                    <td className="border px-4 py-2">Secured loan with the two-wheeler as collateral.</td>
                    <td className="border px-4 py-2">Unsecured loan, no collateral required.</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">Interest Rates</td>
                    <td className="border px-4 py-2">Typically lower, as it is secured by the vehicle.</td>
                    <td className="border px-4 py-2">Higher interest rates due to the lack of collateral.</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">Loan Amount</td>
                    <td className="border px-4 py-2">Restricted to the cost of the two-wheeler being financed.</td>
                    <td className="border px-4 py-2">Flexible, depending on the borrower’s credit profile.</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">Loan Tenure</td>
                    <td className="border px-4 py-2">Shorter loan tenure, usually between 1 to 5 years.</td>
                    <td className="border px-4 py-2">Flexible tenure options, typically ranging from 1 to 5 years.</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">Eligibility Criteria</td>
                    <td className="border px-4 py-2">Based on your ability to repay, credit score, and the vehicle's value.</td>
                    <td className="border px-4 py-2">Based on income, credit score, and repayment history.</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">EMI Amount</td>
                    <td className="border px-4 py-2">Generally lower as the loan amount is smaller and secured.</td>
                    <td className="border px-4 py-2">EMI can be higher, as the loan amount can be more substantial.</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">Processing Fee</td>
                    <td className="border px-4 py-2">Typically lower processing fees.</td>
                    <td className="border px-4 py-2">Higher processing fees as personal loans are unsecured.</td>
                  </tr>
                </tbody>
              </table>
            </div> */}


            <div className="hidden md:block mt-4">
            <table className="w-full text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-4 border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-4 py-2 text-left">Feature</th>
                    <th className="border px-4 py-2 text-left">Two Wheeler Loan</th>
                    <th className="border px-4 py-2 text-left">Personal Loan</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border px-4 py-2">Purpose</td>
                    <td className="border px-4 py-2">Specifically used to finance the purchase of a two-wheeler.</td>
                    <td className="border px-4 py-2">Can be used for any personal need, including vacations, medical expenses, or home renovation.</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">Collateral</td>
                    <td className="border px-4 py-2">Secured loan with the two-wheeler as collateral.</td>
                    <td className="border px-4 py-2">Unsecured loan, no collateral required.</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">Interest Rates</td>
                    <td className="border px-4 py-2">Typically lower, as it is secured by the vehicle.</td>
                    <td className="border px-4 py-2">Higher interest rates due to the lack of collateral.</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">Loan Amount</td>
                    <td className="border px-4 py-2">Restricted to the cost of the two-wheeler being financed.</td>
                    <td className="border px-4 py-2">Flexible, depending on the borrower’s credit profile.</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">Loan Tenure</td>
                    <td className="border px-4 py-2">Shorter loan tenure, usually between 1 to 5 years.</td>
                    <td className="border px-4 py-2">Flexible tenure options, typically ranging from 1 to 5 years.</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">Eligibility Criteria</td>
                    <td className="border px-4 py-2">Based on your ability to repay, credit score, and the vehicle's value.</td>
                    <td className="border px-4 py-2">Based on income, credit score, and repayment history.</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">EMI Amount</td>
                    <td className="border px-4 py-2">Generally lower as the loan amount is smaller and secured.</td>
                    <td className="border px-4 py-2">EMI can be higher, as the loan amount can be more substantial.</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">Processing Fee</td>
                    <td className="border px-4 py-2">Typically lower processing fees.</td>
                    <td className="border px-4 py-2">Higher processing fees as personal loans are unsecured.</td>
                  </tr>
                </tbody>
              </table>
  
</div>

{/* Mobile Cards */}
<div className="md:hidden space-y-4 mt-4">
  {[
  {
    "Feature": "Purpose",
    "TwoWheelerLoan": "Specifically used to finance the purchase of a two-wheeler.",
    "PersonalLoan": "Can be used for any personal need, including vacations, medical expenses, or home renovation."
  },
  {
    "Feature": "Collateral",
    "TwoWheelerLoan": "Secured loan with the two-wheeler as collateral.",
    "PersonalLoan": "Unsecured loan, no collateral required."
  },
  {
    "Feature": "Interest Rates",
    "TwoWheelerLoan": "Typically lower, as it is secured by the vehicle.",
    "PersonalLoan": "Higher interest rates due to the lack of collateral."
  },
  {
    "Feature": "Loan Amount",
    "TwoWheelerLoan": "Restricted to the cost of the two-wheeler being financed.",
    "PersonalLoan": "Flexible, depending on the borrower’s credit profile."
  },
  {
    "Feature": "Loan Tenure",
    "TwoWheelerLoan": "Shorter loan tenure, usually between 1 to 5 years.",
    "Personal Loan": "Flexible tenure options, typically ranging from 1 to 5 years."
  },
  {
    "Feature": "Eligibility Criteria",
    "TwoWheelerLoan": "Based on your ability to repay, credit score, and the vehicle's value.",
    "PersonalLoan": "Based on income, credit score, and repayment history."
  },
  {
    "Feature": "EMI Amount",
    "TwoWheelerLoan": "Generally lower as the loan amount is smaller and secured.",
    "PersonalLoan": "EMI can be higher, as the loan amount can be more substantial."
  },
  {
    "Feature": "Processing Fee",
    "TwoWheelerLoan": "Typically lower processing fees.",
    "PersonalLoan": "Higher processing fees as personal loans are unsecured."
  }

  ].map((item, index) => (
    <div key={index} className="border rounded-lg p-4 shadow-sm bg-white">
      <p className="font-semibold text-gray-700 mb-2">{item.Feature}</p>
      <div className="text-sm text-gray-600">
        <p><span className="font-medium">Two Wheeler Loan:</span> {item.TwoWheelerLoan}</p>
        <p><span className="font-medium">Personal Loan :</span> {item.PersonalLoan}</p>
      </div>
    </div>
  ))}
</div>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Who Should Use The Two Wheeler Loan EMI Calculator?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The Two Wheeler Loan EMI Calculator is an essential tool for anyone planning to finance the purchase of a two-wheeler. Here's who should use it:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>First-Time Bike Buyers:</strong> If you are purchasing a two-wheeler for the first time, the calculator helps you understand your repayment obligations and find a loan option that suits your financial capabilities.</li>
              <li><strong>Budget-Conscious Buyers:</strong> Those who are mindful of their budget will benefit from the calculator. By adjusting loan terms (amount, tenure, interest rate), you can find the EMI that fits comfortably within your monthly income.</li>
              <li><strong>People with Varied Loan Options:</strong> If you are considering loan offers from multiple lenders, the calculator helps you compare different loan amounts, interest rates, and tenures to determine which option gives you the best repayment structure.</li>
              <li><strong>Those Looking to Refinance Their Loan:</strong> If you already have a loan and are considering refinancing it, using the calculator helps you see how changes in the loan amount, tenure, or interest rate will affect your monthly payments.</li>
              <li><strong>Borrowers with a Tight Repayment Schedule:</strong> For those who are worried about sticking to their repayment schedules, the calculator ensures that you plan your finances and avoid any surprises when your EMI is due each month.</li>
            </ul>
          </div>
        </div>

     <div className="w-full md:w-1/3 p-4">
       <div className="bg-white shadow-md rounded-lg overflow-hidden">
         <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
           <h2 className="text-lg font-semibold ">Popular Categories</h2>
         </div>
         <div className="p-2 ">
         <ul>
                                <Link to='/financial-tools-calculators/swp-calculator'>
                                    <li className="py-2  hover:bg-slate-100 border-b border-gray-300">SWP Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/mutul-fund-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Mutual Fund Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/sukanya-samriddhi-yojana-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Samriddhi Yojana Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/fd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">FD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/rd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">RD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/nps-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NPS Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/hra-exemption-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">HRA Exemption Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/retirement-planning-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Retirement Planning Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">EMI Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/simple-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Simple Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/compound-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Compound Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/nsc-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NSC Calculator</li>

                                </Link><Link to='/financial-tools-calculators/step-up-sip-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Step Up Sip Calculator</li>

                                </Link><Link to='/financial-tools-calculators/income-tax-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Income Tax Calculator</li>
                                    
                                </Link><Link to='/financial-tools-calculators/home-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Home Loan Emi Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/car-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Car Loan Emi Calculator</li>
                                </Link>
                                {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                            </ul>
                        </div>
                    </div>
                    <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
                        <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
                            <h2 className="text-lg font-semibold">More Categories</h2>
                        </div>
                        <div className="p-2">
                        <ul>
                                <Link to='/financial-tools-calculators/gratuity-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Gratuity Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/APY-calculator'>
                                    <li className="py-2 border- hover:bg-slate-100 border-gray-300">APY Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/cagr-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">CAGR Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/gst-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">GST Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/flat-vs-reducing-rate-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Flat Vs Reducing Rate Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/equity-margin-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Equity Margin Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/inflation-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Inflation Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/salary-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Salary Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/brokerage-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Brokerage Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/tds-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">TDS Calculator</li>
                                </Link>
             {/* {moreCategories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
           </ul>
         </div>
       </div>

     </div>

   </div>
   </div>
   <div className="max-w-4xl mx-auto lg:px-6 md:px-6 px-2 py-6">
                <h1 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">Frequently Asked Questions</h1>
                <div className="space-y-4">
                    {faqs.map((faq, index) => (
                        <div key={index} className="border rounded-lg overflow-hidden shadow-md">
                            <button
                                onClick={() => toggleAccordion(index)}
                                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                            >
                                {faq.question}
                                <Icon id={index} open={activeIndex} />
                            </button>
                            {activeIndex === index && (
                                <div className="p-4 bg-gray-100">
                                    <p className="text-gray-700">{faq.answer}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
   </>
  );
};

export default TwowheelerLoanEmiCalculator ;
